import { createSelector } from 'reselect';
import { Map as ImmutableMap, Set as ImmutableSet, Record } from 'immutable';
import * as accountSelectors from 'data/accounts/selectors';
import * as investmentQuotesV2Selectors from 'data/investmentQuotesV2/investmentQuotesV2Selectors';

import { INVESTMENT_HOLDINGS_V2_REDUCER_KEY, mkInvestmentHoldingV2 } from './investmentHoldingsV2Types';

export const getResourceStore = (state) => state[INVESTMENT_HOLDINGS_V2_REDUCER_KEY];
export const getLoadPending = (state) => getResourceStore(state).loadPending;
export const isLoading = (state) => getResourceStore(state).isLoading;
export const getLastSyncDate = (state) => getResourceStore(state).lastSyncDate;
export const getInvestmentHoldings = (state) => getResourceStore(state).resourcesById;

export const getInvestmentHoldingsForAccounts = createSelector(
  getInvestmentHoldings,
  (state, accounts) => accountSelectors.accountIds(state, accounts || accountSelectors.getInvestmentAccounts(state)),
  (holdings, accountIds) => holdings.filter((holding) => accountIds.has(holding.accountId)),
);

export const getInvestmentHoldingsBucketized = createSelector(
  (state, holdings) => holdings || getInvestmentHoldings(state),
  (holdings) => ImmutableMap().withMutations((holdingsBucketized) => holdings.reduce((map, investmentHolding) => {
    const key = investmentHolding.symbol || investmentHolding.securityName;
    const existingValue = map.get(key);
    const value = (existingValue && existingValue.merge({
      quantity: typeof existingValue.quantity === 'number' && typeof investmentHolding.quantity === 'number' ? existingValue.quantity + investmentHolding.quantity : undefined,
      marketValue: (existingValue.marketValue || 0) + (investmentHolding.marketValue || 0),
    })) || mkInvestmentHoldingV2({
      symbol: investmentHolding.symbol,
      securityName: investmentHolding.securityName,
      quantity: investmentHolding.quantity,
      marketValue: investmentHolding.marketValue,
      isCash: investmentHolding.isCash,
    });
    return map.set(key, value);
  }, holdingsBucketized)),
);

const holdingValue = (holding, quotes) => {
  let value;
  const quote = quotes.get(holding.symbol);
  if (quote && typeof quote.lastPrice === 'number' && typeof holding.quantity === 'number') {
    value = quote.lastPrice * holding.quantity;
  } else if (typeof holding.marketValue === 'number') {
    value = holding.marketValue;
  }
  return value;
};

const HoldingNode = Record({
  name: undefined,
  symbol: undefined,
  shares: undefined,
  price: undefined,
  value: undefined,
  todayPercent: undefined,
  todayValue: undefined,
  quote: undefined,
  isCash: undefined,
});
export const getInvestmentHoldingNodes = createSelector(
  (state, holdings) => holdings || getInvestmentHoldingsBucketized(state),
  investmentQuotesV2Selectors.getInvestmentQuotes,
  (holdings, quotes) => holdings && holdings.toList().map((holding) => {
    const quote = quotes.get(holding.symbol);
    const name = (quote && quote.name) || holding.securityName || holding.symbol || undefined;
    const value = holdingValue(holding, quotes);
    const todayValue = (quote && typeof quote.amtChange === 'number' && typeof holding.quantity === 'number') ? quote.amtChange * holding.quantity : undefined;
    return HoldingNode({
      name,
      symbol: holding.symbol,
      shares: holding.quantity,
      price: quote && quote.lastPrice,
      value,
      todayPercent: quote && quote.pctChange,
      todayValue,
      quote,
      isCash: holding.isCash,
    });
  }),
);

export const getSymbols = createSelector(
  (state, holdings) => holdings || (state && getInvestmentHoldings(state)),
  (investmentHoldings) => ImmutableSet().withMutations((symbols) => investmentHoldings && investmentHoldings.reduce((set, investmentHolding) => investmentHolding.symbol ? set.add(investmentHolding.symbol) : set, symbols)),
);

export const getHoldingsTotals = createSelector(
  (state, holdings) => holdings || getInvestmentHoldings(state),
  investmentQuotesV2Selectors.getInvestmentQuotes,
  (holdings, quotes) => holdings.reduce((accumulator, holding) => {
    const quote = quotes.get(holding.symbol);
    const value = holdingValue(holding, quotes);
    accumulator.todaysBalance = typeof accumulator.todaysBalance === 'number' ?
      accumulator.todaysBalance + (value || 0) : value;

    const todaysBalanceChange = quote && typeof quote.amtChange === 'number' && typeof holding.quantity === 'number' ? quote.amtChange * holding.quantity : undefined;
    accumulator.todaysBalanceChange = typeof accumulator.todaysBalanceChange === 'number' ?
      accumulator.todaysBalanceChange + (todaysBalanceChange || 0) : todaysBalanceChange;

    return accumulator;
  }, {
    todaysBalance: undefined,
    todaysBalanceChange: undefined,
  }),
);

export const getInvestmentHoldingsBalancesByAccountId = createSelector(
  getInvestmentHoldings,
  investmentQuotesV2Selectors.getInvestmentQuotes,
  function setupResultsCache() {
    let resultsCache;
    return (holdings, quotes) => {
      const resultsFresh = ImmutableMap().withMutations((investmentHoldingsBalancesByAccountId) => {
        holdings.forEach((holding) => investmentHoldingsBalancesByAccountId.set(holding.accountId,
          (investmentHoldingsBalancesByAccountId.get(holding.accountId) || 0) + holdingValue(holding, quotes)));
      });
      resultsCache = resultsFresh.equals(resultsCache) ? resultsCache : resultsFresh;
      return resultsCache;
    };
  }(),
);
